/**
 * ComfyUI API Client
 * 
 * Connects to a local ComfyUI instance for image generation.
 * ComfyUI exposes a REST API on port 8188 by default.
 */

export interface ComfyUIConfig {
    baseUrl: string; // e.g., "http://127.0.0.1:8188"
    enabled: boolean;
}

export interface QueuedPrompt {
    prompt_id: string;
    number: number;
}

export interface ComfyUIProgress {
    status: 'queued' | 'running' | 'completed' | 'error';
    progress?: number; // 0-100
    currentNode?: string;
    error?: string;
}

export interface GenerationOutput {
    images: Array<{
        filename: string;
        subfolder: string;
        type: string;
    }>;
}

export interface HistoryEntry {
    prompt: any;
    outputs: Record<string, GenerationOutput>;
    status: {
        status_str: string;
        completed: boolean;
        messages: Array<[string, any]>;
    };
}

export interface CheckpointInfo {
    name: string;
    path: string;
}

export interface ComfyUISystemInfo {
    system: {
        os: string;
        python_version: string;
        embedded_python: boolean;
    };
    devices: Array<{
        name: string;
        type: string;
        vram_total: number;
        vram_free: number;
    }>;
}

// Default workflow node IDs (for our templates)
const NODE_IDS = {
    CHECKPOINT_LOADER: '4',
    CLIP_TEXT_ENCODE_POSITIVE: '6',
    CLIP_TEXT_ENCODE_NEGATIVE: '7',
    KSAMPLER: '3',
    VAE_DECODE: '8',
    SAVE_IMAGE: '9',
    EMPTY_LATENT: '5',
};

/**
 * ComfyUI API Client class
 */
export class ComfyUIClient {
    private baseUrl: string;
    private clientId: string;

    constructor(config: ComfyUIConfig) {
        let url = config.baseUrl.replace(/\/$/, ''); // Remove trailing slash
        
        // Use Vite proxy for local ComfyUI URLs to bypass CORS in dev
        // This is needed because ComfyUI doesn't allow cross-origin requests
        const isLocalComfyUI = url.includes('127.0.0.1:8188') || url.includes('localhost:8188');
        const isDev = import.meta.env.DEV || window.location.port === '5181' || window.location.port === '5173';
        
        if (isLocalComfyUI && isDev) {
            url = '/api/comfyui';
            console.log('Using ComfyUI proxy for CORS bypass');
        }
        
        this.baseUrl = url;
        this.clientId = this.generateClientId();
    }

    private generateClientId(): string {
        return 'infinite-canvas-' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Test connection to ComfyUI
     */
    async ping(): Promise<boolean> {
        try {
            const response = await fetch(`${this.baseUrl}/system_stats`, {
                method: 'GET',
                signal: AbortSignal.timeout(5000),
            });
            return response.ok;
        } catch (e) {
            console.error('ComfyUI ping failed:', e);
            return false;
        }
    }

    /**
     * Get system information
     */
    async getSystemInfo(): Promise<ComfyUISystemInfo | null> {
        try {
            const response = await fetch(`${this.baseUrl}/system_stats`);
            if (!response.ok) return null;
            return await response.json();
        } catch (e) {
            console.error('Failed to get ComfyUI system info:', e);
            return null;
        }
    }

    /**
     * Get available model checkpoints
     */
    async getCheckpoints(): Promise<string[]> {
        try {
            const response = await fetch(`${this.baseUrl}/object_info/CheckpointLoaderSimple`);
            if (!response.ok) return [];
            const data = await response.json();
            return data?.CheckpointLoaderSimple?.input?.required?.ckpt_name?.[0] || [];
        } catch (e) {
            console.error('Failed to get checkpoints:', e);
            return [];
        }
    }

    /**
     * Get available UNET models
     */
    async getUnets(): Promise<string[]> {
        try {
            const response = await fetch(`${this.baseUrl}/object_info/UNETLoader`);
            if (!response.ok) return [];
            const data = await response.json();
            return data?.UNETLoader?.input?.required?.unet_name?.[0] || [];
        } catch (e) {
            console.error('Failed to get UNETs:', e);
            return [];
        }
    }

    /**
     * Get available CLIP models
     */
    async getClips(): Promise<string[]> {
        try {
            // Try DualCLIPLoader first (common for FLUX)
            const response = await fetch(`${this.baseUrl}/object_info/DualCLIPLoader`);
            if (response.ok) {
                const data = await response.json();
                return data?.DualCLIPLoader?.input?.required?.clip_name1?.[0] || [];
            }
            
            // Fallback to CLIPLoader
            const response2 = await fetch(`${this.baseUrl}/object_info/CLIPLoader`);
            if (response2.ok) {
                const data = await response2.json();
                return data?.CLIPLoader?.input?.required?.clip_name?.[0] || [];
            }
            return [];
        } catch (e) {
            console.error('Failed to get CLIPs:', e);
            return [];
        }
    }

    /**
     * Get available VAE models
     */
    async getVaes(): Promise<string[]> {
        try {
            const response = await fetch(`${this.baseUrl}/object_info/VAELoader`);
            if (!response.ok) return [];
            const data = await response.json();
            return data?.VAELoader?.input?.required?.vae_name?.[0] || [];
        } catch (e) {
            console.error('Failed to get VAEs:', e);
            return [];
        }
    }

    /**
     * Get available samplers
     */
    async getSamplers(): Promise<string[]> {
        try {
            const response = await fetch(`${this.baseUrl}/object_info/KSampler`);
            if (!response.ok) return [];
            const data = await response.json();
            return data?.KSampler?.input?.required?.sampler_name?.[0] || [];
        } catch (e) {
            console.error('Failed to get samplers:', e);
            return [];
        }
    }

    /**
     * Get available schedulers
     */
    async getSchedulers(): Promise<string[]> {
        try {
            const response = await fetch(`${this.baseUrl}/object_info/KSampler`);
            if (!response.ok) return [];
            const data = await response.json();
            return data?.KSampler?.input?.required?.scheduler?.[0] || [];
        } catch (e) {
            console.error('Failed to get schedulers:', e);
            return [];
        }
    }

    /**
     * Queue a prompt/workflow for execution
     */
    async queuePrompt(workflow: object): Promise<QueuedPrompt> {
        const response = await fetch(`${this.baseUrl}/prompt`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt: workflow,
                client_id: this.clientId,
            }),
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Failed to queue prompt: ${error}`);
        }

        return await response.json();
    }

    /**
     * Get history/status of a prompt
     */
    async getHistory(promptId: string): Promise<HistoryEntry | null> {
        try {
            const response = await fetch(`${this.baseUrl}/history/${promptId}`);
            if (!response.ok) return null;
            const data = await response.json();
            return data[promptId] || null;
        } catch (e) {
            console.error('Failed to get history:', e);
            return null;
        }
    }

    /**
     * Get queue status
     */
    async getQueueStatus(): Promise<{ queue_running: any[]; queue_pending: any[] }> {
        try {
            const response = await fetch(`${this.baseUrl}/queue`);
            if (!response.ok) return { queue_running: [], queue_pending: [] };
            return await response.json();
        } catch (e) {
            console.error('Failed to get queue status:', e);
            return { queue_running: [], queue_pending: [] };
        }
    }

    /**
     * Download a generated image
     */
    async getImage(filename: string, subfolder: string = '', type: string = 'output'): Promise<Blob> {
        const params = new URLSearchParams({
            filename,
            subfolder,
            type,
        });
        const response = await fetch(`${this.baseUrl}/view?${params}`);
        if (!response.ok) {
            throw new Error(`Failed to download image: ${response.statusText}`);
        }
        return await response.blob();
    }

    /**
     * Upload an image for use as input (e.g., img2img, ControlNet)
     */
    async uploadImage(file: Blob, filename: string, overwrite: boolean = true): Promise<{ name: string; subfolder: string; type: string }> {
        const formData = new FormData();
        formData.append('image', file, filename);
        formData.append('overwrite', overwrite.toString());

        const response = await fetch(`${this.baseUrl}/upload/image`, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Failed to upload image: ${response.statusText}`);
        }

        return await response.json();
    }

    /**
     * Cancel current generation
     */
    async interrupt(): Promise<void> {
        await fetch(`${this.baseUrl}/interrupt`, { method: 'POST' });
    }

    /**
     * Clear the queue
     */
    async clearQueue(): Promise<void> {
        await fetch(`${this.baseUrl}/queue`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ clear: true }),
        });
    }

    /**
     * Poll for generation completion
     */
    async waitForCompletion(
        promptId: string,
        onProgress?: (progress: ComfyUIProgress) => void,
        timeoutMs: number = 300000 // 5 minutes default
    ): Promise<HistoryEntry> {
        const startTime = Date.now();
        
        while (Date.now() - startTime < timeoutMs) {
            // Check queue status
            const queue = await this.getQueueStatus();
            const isRunning = queue.queue_running.some(item => item[1] === promptId);
            const isPending = queue.queue_pending.some(item => item[1] === promptId);

            if (isRunning) {
                onProgress?.({ status: 'running', progress: 50 });
            } else if (isPending) {
                onProgress?.({ status: 'queued', progress: 0 });
            } else {
                // Not in queue, check history
                const history = await this.getHistory(promptId);
                if (history) {
                    if (history.status?.completed) {
                        onProgress?.({ status: 'completed', progress: 100 });
                        return history;
                    }
                    if (history.status?.status_str === 'error') {
                        const errorMsg = history.status.messages?.find(m => m[0] === 'execution_error')?.[1]?.exception_message || 'Unknown error';
                        throw new Error(errorMsg);
                    }
                }
            }

            // Wait before next poll
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        throw new Error('Generation timed out');
    }
}

/**
 * Build a basic img2img workflow
 */
export const buildImg2ImgWorkflow = (params: {
    checkpoint: string;
    imageName: string; // Uploaded image filename
    prompt: string;
    negativePrompt?: string;
    width?: number;
    height?: number;
    steps?: number;
    cfg?: number;
    seed?: number;
    denoise?: number; // 0.0 to 1.0 (strength of change)
    sampler?: string;
    scheduler?: string;
}): object => {
    const {
        checkpoint,
        imageName,
        prompt,
        negativePrompt = '',
        steps = 20,
        cfg = 7,
        seed = Math.floor(Math.random() * 2147483647),
        denoise = 0.6,
        sampler = 'euler',
        scheduler = 'normal',
    } = params;

    return {
        "3": {
            "inputs": {
                "seed": seed,
                "steps": steps,
                "cfg": cfg,
                "sampler_name": sampler,
                "scheduler": scheduler,
                "denoise": denoise,
                "model": ["4", 0],
                "positive": ["6", 0],
                "negative": ["7", 0],
                "latent_image": ["10", 0] // Connect to VAE Encode output
            },
            "class_type": "KSampler",
            "_meta": { "title": "KSampler" }
        },
        "4": {
            "inputs": {
                "ckpt_name": checkpoint
            },
            "class_type": "CheckpointLoaderSimple",
            "_meta": { "title": "Load Checkpoint" }
        },
        "6": {
            "inputs": {
                "text": prompt,
                "clip": ["4", 1]
            },
            "class_type": "CLIPTextEncode",
            "_meta": { "title": "CLIP Text Encode (Prompt)" }
        },
        "7": {
            "inputs": {
                "text": negativePrompt,
                "clip": ["4", 1]
            },
            "class_type": "CLIPTextEncode",
            "_meta": { "title": "CLIP Text Encode (Negative)" }
        },
        "8": {
            "inputs": {
                "samples": ["3", 0],
                "vae": ["4", 2]
            },
            "class_type": "VAEDecode",
            "_meta": { "title": "VAE Decode" }
        },
        "9": {
            "inputs": {
                "filename_prefix": "ComfyUI_img2img",
                "images": ["8", 0]
            },
            "class_type": "SaveImage",
            "_meta": { "title": "Save Image" }
        },
        "10": { // VAE Encode
            "inputs": {
                "pixels": ["11", 0],
                "vae": ["4", 2]
            },
            "class_type": "VAEEncode",
            "_meta": { "title": "VAE Encode" }
        },
        "11": { // Load Image
            "inputs": {
                "image": imageName,
                "upload": "image"
            },
            "class_type": "LoadImage",
            "_meta": { "title": "Load Image" }
        }
    };
};

/**
 * Build a basic txt2img workflow
 */
export const buildTxt2ImgWorkflow = (params: {
    checkpoint: string;
    prompt: string;
    negativePrompt?: string;
    width?: number;
    height?: number;
    steps?: number;
    cfg?: number;
    seed?: number;
    sampler?: string;
    scheduler?: string;
}): object => {
    const {
        checkpoint,
        prompt,
        negativePrompt = '',
        width = 1024,
        height = 1024,
        steps = 20,
        cfg = 7,
        seed = Math.floor(Math.random() * 2147483647),
        sampler = 'euler',
        scheduler = 'normal',
    } = params;

    return {
        "3": {
            "inputs": {
                "seed": seed,
                "steps": steps,
                "cfg": cfg,
                "sampler_name": sampler,
                "scheduler": scheduler,
                "denoise": 1,
                "model": ["4", 0],
                "positive": ["6", 0],
                "negative": ["7", 0],
                "latent_image": ["5", 0]
            },
            "class_type": "KSampler",
            "_meta": { "title": "KSampler" }
        },
        "4": {
            "inputs": {
                "ckpt_name": checkpoint
            },
            "class_type": "CheckpointLoaderSimple",
            "_meta": { "title": "Load Checkpoint" }
        },
        "5": {
            "inputs": {
                "width": width,
                "height": height,
                "batch_size": 1
            },
            "class_type": "EmptyLatentImage",
            "_meta": { "title": "Empty Latent Image" }
        },
        "6": {
            "inputs": {
                "text": prompt,
                "clip": ["4", 1]
            },
            "class_type": "CLIPTextEncode",
            "_meta": { "title": "CLIP Text Encode (Prompt)" }
        },
        "7": {
            "inputs": {
                "text": negativePrompt,
                "clip": ["4", 1]
            },
            "class_type": "CLIPTextEncode",
            "_meta": { "title": "CLIP Text Encode (Negative)" }
        },
        "8": {
            "inputs": {
                "samples": ["3", 0],
                "vae": ["4", 2]
            },
            "class_type": "VAEDecode",
            "_meta": { "title": "VAE Decode" }
        },
        "9": {
            "inputs": {
                "filename_prefix": "ComfyUI",
                "images": ["8", 0]
            },
            "class_type": "SaveImage",
            "_meta": { "title": "Save Image" }
        }
    };
};

/**
 * Build a FLUX workflow using UNETLoader (for separate model components)
 */
export const buildFluxUnetWorkflow = (params: {
    unet: string;
    clip1: string; // usually t5xxl
    clip2: string; // usually clip_l
    vae: string;
    prompt: string;
    width?: number;
    height?: number;
    steps?: number;
    guidance?: number;
    seed?: number;
}): object => {
    const {
        unet,
        clip1,
        clip2,
        vae,
        prompt,
        width = 1024,
        height = 1024,
        steps = 4,
        guidance = 3.5,
        seed = Math.floor(Math.random() * 2147483647),
    } = params;

    // Based on the JSON workflow provided
    return {
        "6": { // CLIP Text Encode
            "inputs": {
                "text": prompt,
                "clip": ["11", 0]
            },
            "class_type": "CLIPTextEncode",
            "_meta": { "title": "CLIP Text Encode (Prompt)" }
        },
        "8": { // VAE Decode
            "inputs": {
                "samples": ["13", 0],
                "vae": ["10", 0]
            },
            "class_type": "VAEDecode",
            "_meta": { "title": "VAEDecode" }
        },
        "9": { // Save Image
            "inputs": {
                "filename_prefix": "FLUX_SCHNELL",
                "images": ["8", 0]
            },
            "class_type": "SaveImage",
            "_meta": { "title": "SaveImage" }
        },
        "10": { // Load VAE
            "inputs": {
                "vae_name": vae
            },
            "class_type": "VAELoader",
            "_meta": { "title": "VAELoader" }
        },
        "11": { // DualCLIPLoader
            "inputs": {
                "clip_name1": clip1,
                "clip_name2": clip2,
                "type": "flux"
            },
            "class_type": "DualCLIPLoader",
            "_meta": { "title": "DualCLIPLoader" }
        },
        "12": { // Load UNET
            "inputs": {
                "unet_name": unet,
                "weight_dtype": "fp8_e4m3fn" // Common for Flux Schnell, maybe make configurable?
            },
            "class_type": "UNETLoader",
            "_meta": { "title": "UNETLoader" }
        },
        "13": { // SamplerCustomAdvanced
            "inputs": {
                "noise": ["25", 0],
                "guider": ["22", 0],
                "sampler": ["16", 0],
                "sigmas": ["17", 0],
                "latent_image": ["31", 0]
            },
            "class_type": "SamplerCustomAdvanced",
            "_meta": { "title": "SamplerCustomAdvanced" }
        },
        "16": { // KSamplerSelect
            "inputs": {
                "sampler_name": "euler"
            },
            "class_type": "KSamplerSelect",
            "_meta": { "title": "KSamplerSelect" }
        },
        "17": { // BasicScheduler
            "inputs": {
                "scheduler": "simple",
                "steps": steps,
                "denoise": 1,
                "model": ["12", 0]
            },
            "class_type": "BasicScheduler",
            "_meta": { "title": "BasicScheduler" }
        },
        "22": { // BasicGuider
            "inputs": {
                "model": ["12", 0],
                "conditioning": ["6", 0]
            },
            "class_type": "BasicGuider",
            "_meta": { "title": "BasicGuider" }
        },
        "25": { // RandomNoise
            "inputs": {
                "noise_seed": seed
            },
            "class_type": "RandomNoise",
            "_meta": { "title": "RandomNoise" }
        },
        "31": { // Empty Latent Image
            "inputs": {
                "width": width,
                "height": height,
                "batch_size": 1
            },
            "class_type": "EmptyLatentImage",
            "_meta": { "title": "EmptyLatentImage" }
        }
    };
};

/**
 * Build a FLUX-specific workflow (uses different nodes)
 */
export const buildFluxWorkflow = (params: {
    checkpoint: string;
    prompt: string;
    width?: number;
    height?: number;
    steps?: number;
    guidance?: number;
    seed?: number;
}): object => {
    const {
        checkpoint,
        prompt,
        width = 1024,
        height = 1024,
        steps = 20,
        guidance = 3.5,
        seed = Math.floor(Math.random() * 2147483647),
    } = params;

    // FLUX uses a different workflow structure
    // This is a simplified version - real FLUX needs CLIP + T5 encoders
    return {
        "4": {
            "inputs": {
                "ckpt_name": checkpoint
            },
            "class_type": "CheckpointLoaderSimple",
            "_meta": { "title": "Load Checkpoint" }
        },
        "5": {
            "inputs": {
                "width": width,
                "height": height,
                "batch_size": 1
            },
            "class_type": "EmptyLatentImage",
            "_meta": { "title": "Empty Latent Image" }
        },
        "6": {
            "inputs": {
                "text": prompt,
                "clip": ["4", 1]
            },
            "class_type": "CLIPTextEncode",
            "_meta": { "title": "CLIP Text Encode (Prompt)" }
        },
        "7": {
            "inputs": {
                "text": "",
                "clip": ["4", 1]
            },
            "class_type": "CLIPTextEncode",
            "_meta": { "title": "CLIP Text Encode (Negative)" }
        },
        "3": {
            "inputs": {
                "seed": seed,
                "steps": steps,
                "cfg": guidance,
                "sampler_name": "euler",
                "scheduler": "simple",
                "denoise": 1,
                "model": ["4", 0],
                "positive": ["6", 0],
                "negative": ["7", 0],
                "latent_image": ["5", 0]
            },
            "class_type": "KSampler",
            "_meta": { "title": "KSampler" }
        },
        "8": {
            "inputs": {
                "samples": ["3", 0],
                "vae": ["4", 2]
            },
            "class_type": "VAEDecode",
            "_meta": { "title": "VAE Decode" }
        },
        "9": {
            "inputs": {
                "filename_prefix": "FLUX",
                "images": ["8", 0]
            },
            "class_type": "SaveImage",
            "_meta": { "title": "Save Image" }
        }
    };
};

/**
 * Convert aspect ratio string to width/height
 */
export const aspectRatioToSize = (ratio: string, baseSize: number = 1024): { width: number; height: number } => {
    const [w, h] = ratio.split(':').map(Number);
    if (!w || !h) return { width: baseSize, height: baseSize };

    // Calculate dimensions that fit within baseSize while maintaining ratio
    if (w > h) {
        return { width: baseSize, height: Math.round(baseSize * (h / w)) };
    } else if (h > w) {
        return { width: Math.round(baseSize * (w / h)), height: baseSize };
    }
    return { width: baseSize, height: baseSize };
};

/**
 * Convert ComfyUI workflow JSON (UI export format) to API format
 * 
 * UI format has: nodes[] with id, type, inputs, widgets_values; links[] with connection info
 * API format has: { nodeId: { class_type, inputs: { inputName: [sourceNodeId, sourceSlot] | value } } }
 */
export function convertWorkflowToApi(workflow: any): Record<string, any> {
    const apiFormat: Record<string, any> = {};
    const nodes = workflow.nodes || [];
    const links = workflow.links || [];
    
    // Non-executable node types that should be skipped (UI-only or passthrough nodes)
    const skipNodeTypes = new Set([
        'Note',                 // Comment/annotation nodes
        'PrimitiveNode',        // Primitive value nodes (handled differently)
        'Reroute',              // Standard reroute (passthrough)
        'Reroute (rgthree)',    // rgthree reroute (passthrough)
    ]);
    
    // Build node lookup by ID for quick access
    const nodeById = new Map<number, any>();
    for (const node of nodes) {
        nodeById.set(node.id, node);
    }
    
    // Build link lookup: linkId -> [sourceNodeId, sourceSlot]
    const linkMap = new Map<number, [number, number]>();
    for (const link of links) {
        // link format: [linkId, sourceNodeId, sourceSlot, targetNodeId, targetSlot, type]
        if (Array.isArray(link) && link.length >= 3) {
            linkMap.set(link[0], [link[1], link[2]]);
        }
    }
    
    // Function to resolve source through Reroute nodes
    // Returns [actualSourceNodeId, actualSourceSlot]
    const resolveSource = (sourceNodeId: number, sourceSlot: number, visited = new Set<number>()): [number, number] => {
        // Prevent infinite loops
        if (visited.has(sourceNodeId)) {
            return [sourceNodeId, sourceSlot];
        }
        visited.add(sourceNodeId);
        
        const sourceNode = nodeById.get(sourceNodeId);
        if (!sourceNode) {
            return [sourceNodeId, sourceSlot];
        }
        
        // If source is a Reroute node, follow its input to find the real source
        if (skipNodeTypes.has(sourceNode.type) && sourceNode.type !== 'Note' && sourceNode.type !== 'PrimitiveNode') {
            // Reroute nodes have a single input - find it
            if (sourceNode.inputs && sourceNode.inputs.length > 0) {
                const rerouteInput = sourceNode.inputs[0];
                if (rerouteInput.link != null) {
                    const upstreamSource = linkMap.get(rerouteInput.link);
                    if (upstreamSource) {
                        // Recursively resolve through any chained Reroute nodes
                        return resolveSource(upstreamSource[0], upstreamSource[1], visited);
                    }
                }
            }
        }
        
        return [sourceNodeId, sourceSlot];
    };
    
    for (const node of nodes) {
        // Skip non-executable nodes (UI-only nodes like Note, Reroute)
        if (skipNodeTypes.has(node.type)) {
            continue;
        }
        const nodeInputs: Record<string, any> = {};
        
        // Track which input indices have been filled by connections
        const connectedInputNames = new Set<string>();
        
        // Process input connections from the links
        if (node.inputs && Array.isArray(node.inputs)) {
            for (const input of node.inputs) {
                if (input.link != null) {
                    const source = linkMap.get(input.link);
                    if (source) {
                        // Resolve through any Reroute nodes to find the actual source
                        const [actualSourceId, actualSourceSlot] = resolveSource(source[0], source[1]);
                        nodeInputs[input.name] = [String(actualSourceId), actualSourceSlot];
                        connectedInputNames.add(input.name);
                    }
                }
            }
        }
        
        // Process widget values - these are the non-connected inputs
        // Widget values are stored in order, matching the widget definitions
        if (node.widgets_values && Array.isArray(node.widgets_values)) {
            // We need to map widget_values to input names
            // This is tricky because we don't have the node definition here
            // ComfyUI stores widget values in a specific order based on node type
            // For now, we'll try to match them based on the input definitions
            
            // Get list of inputs that are typically widgets (not connections)
            // Common widget inputs we know about:
            const widgetInputMap: Record<string, string[]> = {
                // Samplers
                'KSampler': ['seed', 'control_after_generate', 'steps', 'cfg', 'sampler_name', 'scheduler', 'denoise'],
                'KSamplerAdvanced': ['add_noise', 'noise_seed', 'control_after_generate', 'steps', 'cfg', 'sampler_name', 'scheduler', 'start_at_step', 'end_at_step', 'return_with_leftover_noise'],
                'KSamplerSelect': ['sampler_name'],
                'SamplerCustomAdvanced': [], // Uses connected inputs only
                // Loaders
                'CheckpointLoaderSimple': ['ckpt_name'],
                'VAELoader': ['vae_name'],
                'DualCLIPLoader': ['clip_name1', 'clip_name2', 'type'],
                'UNETLoader': ['unet_name', 'weight_dtype'],
                'UpscaleModelLoader': ['model_name'],
                'DownloadAndLoadFlorence2Model': ['model', 'precision', 'attention'],
                // Text/CLIP
                'CLIPTextEncode': ['text'],
                'TextConcat': ['delimiter', 'text1', 'text2', 'text3', 'text4', 'text5'],
                'Wildcard Processor': ['prompt', 'seed', 'control_after_generate'],  // Fixed: 'text' -> 'prompt'
                'SDXL Prompt Styler (JPS)': ['text_positive_g', 'text_positive_l', 'text_negative', 'artist', 'movie', 'style', 'fooocus_enhance', 'fooocus_negative', 'universal_negative'],  // Fixed: added missing inputs
                'ShowText|pysssss': ['text'],
                'easy showAnything': [], // Display only, uses connected input
                // Latent
                'EmptyLatentImage': ['width', 'height', 'batch_size'],
                'Empty Latent Ratio Select SDXL': ['ratio_selected', 'batch_size'],  // Fixed: 'ratio' -> 'ratio_selected'
                // Schedulers
                'BasicScheduler': ['scheduler', 'steps', 'denoise'],
                'RandomNoise': ['noise_seed', 'control_after_generate'],
                'SplitSigmas': ['step'],
                // Image processing
                'SaveImage': ['filename_prefix'],
                'ImageScale': ['upscale_method', 'width', 'height', 'crop'],
                'ImageScaleBy': ['upscale_method', 'scale_by'],
                'ImageUpscaleWithModel': [], // Uses connected inputs only
                'RepeatImageBatch': ['amount'],
                'Image Comparer (rgthree)': [], // Uses connected inputs only
                // FLUX specific
                'ModelSamplingFlux': ['max_shift', 'base_shift', 'width', 'height'],  // Fixed: added missing inputs
                'FluxGuidance': ['guidance'],
                'BasicGuider': [], // Uses connected inputs only
                // VAE
                'VAEDecode': [], // Uses connected inputs only
                'VAEEncode': [], // Uses connected inputs only
                // Florence/Vision
                'Florence2Run': ['text_input', 'task', 'fill_mask', 'keep_model_loaded', 'max_new_tokens', 'num_beams', 'do_sample', 'output_mask_select'],
                // Reroute (passthrough)
                'Reroute (rgthree)': [], // Passthrough node
            };
            
            const nodeType = node.type || '';
            const widgetNames = widgetInputMap[nodeType];
            
            if (widgetNames) {
                // Map widget values to their names
                // IMPORTANT: widgets_values array has slots for ALL widgets, including connected ones
                // So we must always increment index, but only assign if not connected
                let widgetIndex = 0;
                for (const widgetName of widgetNames) {
                    if (widgetIndex < node.widgets_values.length) {
                        // Only assign the value if this input is NOT connected
                        if (!connectedInputNames.has(widgetName)) {
                            const value = node.widgets_values[widgetIndex];
                            if (value !== undefined && value !== null) {
                                nodeInputs[widgetName] = value;
                            }
                        }
                        // Always increment - widgets_values has slots for connected inputs too
                        widgetIndex++;
                    }
                }
            } else {
                // For unknown node types, try to use input definitions if available
                // or just assign values by index to non-connected inputs
                if (node.inputs && Array.isArray(node.inputs)) {
                    // Some nodes define widget inputs in the inputs array with widget property
                    let widgetIndex = 0;
                    for (const input of node.inputs) {
                        if (input.widget && !connectedInputNames.has(input.name)) {
                            if (widgetIndex < node.widgets_values.length) {
                                nodeInputs[input.name] = node.widgets_values[widgetIndex];
                                widgetIndex++;
                            }
                        }
                    }
                }
            }
        }
        
        // Sanitize SaveImage filename_prefix to avoid Windows path issues
        // Replace date patterns like %date:yyyy-MM-dd% with simple timestamps
        if (node.type === 'SaveImage' && nodeInputs.filename_prefix) {
            const original = nodeInputs.filename_prefix;
            // Replace %date:...% patterns and other problematic characters
            let sanitized = original
                .replace(/%date:[^%]+%/g, '')  // Remove date patterns
                .replace(/[<>:"|?*]/g, '_')     // Replace invalid Windows chars
                .replace(/\/+/g, '/')           // Normalize slashes
                .replace(/^\/+|\/+$/g, '');     // Trim leading/trailing slashes
            
            // If empty after sanitization, use a default
            if (!sanitized || sanitized === '/') {
                sanitized = 'ComfyUI/output';
            }
            
            nodeInputs.filename_prefix = sanitized;
            if (original !== sanitized) {
                console.log(`Sanitized SaveImage prefix: "${original}" -> "${sanitized}"`);
            }
        }
        
        apiFormat[String(node.id)] = {
            class_type: node.type,
            inputs: nodeInputs,
            _meta: { title: node.title || node.type }
        };
    }
    
    return apiFormat;
}

// Singleton client instance
let comfyUIClientInstance: ComfyUIClient | null = null;

/**
 * Get or create the ComfyUI client
 */
// Track the original URL used to create the client
let comfyUIClientOriginalUrl: string | null = null;

export const getComfyUIClient = (baseUrl?: string): ComfyUIClient => {
    const url = baseUrl || localStorage.getItem('comfyui_url') || 'http://127.0.0.1:8188';
    
    // Create new client if URL changed or no client exists
    if (!comfyUIClientInstance || comfyUIClientOriginalUrl !== url) {
        comfyUIClientInstance = new ComfyUIClient({ baseUrl: url } as any);
        comfyUIClientOriginalUrl = url;
    }
    
    return comfyUIClientInstance;
};

/**
 * Save ComfyUI configuration
 */
export const saveComfyUIConfig = (config: ComfyUIConfig): void => {
    localStorage.setItem('comfyui_url', config.baseUrl);
    localStorage.setItem('comfyui_enabled', config.enabled.toString());
    // Reset client to use new URL
    comfyUIClientInstance = null;
    comfyUIClientOriginalUrl = null;
};

/**
 * Get ComfyUI configuration
 */
export const getComfyUIConfig = (): ComfyUIConfig => {
    const url = localStorage.getItem('comfyui_url') || 'http://127.0.0.1:8188';
    const enabled = localStorage.getItem('comfyui_enabled') === 'true';
    return {
        baseUrl: url,
        enabled,
    };
};

export default ComfyUIClient;

